import React, { useContext, useEffect, useState } from 'react';
import MomentUtils from '@date-io/moment';
import { Box } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { debounce } from 'lodash';
import Tooltip from '@material-ui/core/Tooltip';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { renderRecurringDatesOnCalendar } from '../../RecurringAppointments/RecurringPlanCalendar';
import { QuickAppointmentContext } from '../../Context';
import { apiGet } from '../../brainApi';

const useStyles = makeStyles(() => ({
  loadingIconBox: {
    paddingTop: 70,
    paddingBottom: 70,
  },
  disableDate: {
    '& .MuiPickersDay-dayDisabled': {
      color: `#000000`,
      opacity: 0.2,
    },
  },
  selectedDate: {
    '& .MuiPickersDay-daySelected': {
      background: '#296690 !important',
      color: '#ffffff !important',
    },
  },
  selectedDateInConflict: {
    '& .MuiPickersDay-daySelected': {
      background: '#E5E3E3 !important',
      color: '#000000 !important',
      border: '3px solid #296690',
      height: '30px',
      width: '30px',
    },
  },
  dateAvailable: {
    '& .MuiPickersDay-day': {
      background: '#BAE2E7',
      color: '#000000',
    },
  },
  dateInConflict: {
    '& .MuiPickersDay-day': {
      background: '#E5E3E3',
      color: '#000000',
    },
  },
  dateInPattern: {
    '& .MuiPickersDay-day': {
      border: '3px solid #296690',
      height: '30px',
      width: '30px',
    },
  },
  conflictInPattern: {
    '& .MuiPickersDay-day': {
      background: '#F39D9D',
      color: '#000000',
    },
  },
}));

const AvailabilityWindowsCalendar = ({
  selectedDate,
  setSelectedDate,
  shouldDisableDate,
  record,
}) => {
  const {
    loadingDates,
    datesToCreate,
    recurringDatesWithConflicts,
    isRecurring,
    scheduleData,
    setScheduleData,
    onRruleChange,
    setLoadingDates,
    isUsingOptimalSchedule,
    isLoadingAdditionalDates,
    setIsLoadingAdditionalDates,
    availableDatesToShow,
    setAvailableDatesToShow,
  } = useContext(QuickAppointmentContext);

  const {
    service_offering_ids,
    home_address_id,
    client_location_id,
    total_labor_hours,
  } = record;

  const classes = useStyles();

  const [allDatesHaveLoaded, setAllDatesHaveLoaded] = useState(false);
  const [
    availableDateMapForAllServiceVehicles,
    setAvailableDateMapForAllServiceVehicles,
  ] = useState({});
  const [disableCalendarNavigation, setDisableCalendarNavigation] = useState(
    false,
  );

  useEffect(() => {
    if (client_location_id || home_address_id) {
      getDates();
    }
  }, [client_location_id, home_address_id]);

  const handleDateChange = (selected_date) => {
    if (availableDatesToShow === null || selected_date === null) {
      // Skip if no date information is loaded yet
      return;
    }

    const formattedDate = moment(selected_date)?.format('YYYY-MM-DD');

    if (shouldDisableDate(selected_date)) {
      setSelectedDate(null);
      if (scheduleData?.selected_date !== null) {
        setScheduleData((prev) => ({
          ...prev,
          selected_date: null,
        }));
      }
    } else {
      const selectedSchedule = availableDatesToShow.find(
        (d) => d.date === formattedDate,
      );
      if (!selectedSchedule) {
        // Shouldn't hit this - dates without any details should be disabled on the calendar
        setSelectedDate(null);
        return;
      }
      const serviceVehicle = {
        service_vehicle_id: selectedSchedule.service_vehicle_id,
        service_vehicle_name: selectedSchedule.service_vehicle_name,
      };
      setSelectedDate(selected_date);
      onRruleChange(
        'dtstart',
        new Date(`${selected_date.format('YYYY-MM-DD')}T00:00:00`),
      );
      setScheduleData((prev) => ({
        ...prev,
        selected_date,
        ...serviceVehicle,
      }));
    }
  };

  const selectFirstAvailableDate = () => {
    let firstAvailableDate = null;
    if (availableDatesToShow?.length > 0) {
      firstAvailableDate = moment(
        availableDatesToShow.sort((a, b) => (a.date > b.date ? 1 : -1))[0].date,
        'YYYY-MM-DD',
      );
    }
    handleDateChange(firstAvailableDate);
  };

  // Each time the available date list is changed, select a new date if the selected date is no longer available
  useEffect(() => {
    if (availableDatesToShow) {
      const isSelectedDateInFilter = availableDatesToShow.find(
        (d) => d.date === scheduleData?.selected_date?.format('YYYY-MM-DD'),
      );
      if (!isSelectedDateInFilter) {
        selectFirstAvailableDate();
      }
    }
  }, [availableDatesToShow]);

  // When an admin is manually selecting the service vehicle, filter to just dates for that vehicle
  useEffect(() => {
    if (
      !isUsingOptimalSchedule &&
      scheduleData?.service_vehicle_id &&
      Object.keys(availableDateMapForAllServiceVehicles)?.length > 0
    ) {
      const filteredAvailableDates = Object.keys(
        availableDateMapForAllServiceVehicles,
      )
        .map((d) => {
          if (availableDateMapForAllServiceVehicles[d]?.length > 0) {
            const selectedSchedule = availableDateMapForAllServiceVehicles[
              d
            ].find(
              (s) => s.service_vehicle_id === scheduleData?.service_vehicle_id,
            );
            if (selectedSchedule) {
              return { ...selectedSchedule, date: d };
            }
          }
        })
        .filter((d) => d);
      setAvailableDatesToShow(filteredAvailableDates);
    } else {
      const dateList = getOptimalSchedules(
        availableDateMapForAllServiceVehicles,
      );
      setAvailableDatesToShow(dateList);
      // If switching back to optimal, select the optimal service vehicle again
      if (scheduleData?.selected_date && dateList.length > 0) {
        const selectedSchedule = dateList.find(
          (d) =>
            d.date === moment(scheduleData?.selected_date).format('YYYY-MM-DD'),
        );
        if (selectedSchedule) {
          setScheduleData((prev) => ({
            ...prev,
            service_vehicle_id: selectedSchedule.service_vehicle_id,
            service_vehicle_name: selectedSchedule.service_vehicle_name,
          }));
        }
      }
    }
  }, [
    scheduleData?.service_vehicle_id,
    availableDateMapForAllServiceVehicles,
    isUsingOptimalSchedule,
  ]);

  const getDates = (startDateMoment = undefined) => {
    let shouldLoadAdditionalDates = true;
    const params = {
      use_new_version: true,
      requested_service_offering_ids: service_offering_ids,
      total_labor_hours,
    };
    if (client_location_id) {
      params.client_location_id = client_location_id;
    } else {
      params.home_address_id = home_address_id;
    }
    const startOfMonth = moment(startDateMoment).startOf('month');
    const endOfMonth = moment(startDateMoment).endOf('month');
    const endOfNextMonth = moment(startDateMoment)
      .add(1, 'month')
      .endOf('month');
    if (startDateMoment) {
      const savedDates = Object.keys(
        availableDateMapForAllServiceVehicles,
      ).sort();
      // If month being viewed is not within loaded dates, load the current and next months
      shouldLoadAdditionalDates =
        endOfMonth >= moment() &&
        !savedDates.some(
          (date) =>
            moment(date).month() === endOfMonth.month() &&
            moment(date).year() === endOfMonth.year(),
        );
    }
    params.start_date = startOfMonth.format('YYYY-MM-DD');
    params.end_date = endOfNextMonth.format('YYYY-MM-DD');

    if (service_offering_ids?.length > 0) {
      // Only show the loading indicator overlay on the first load to not interrupt the user's workflow
      if (!startDateMoment) {
        setLoadingDates(true);
      }

      if (shouldLoadAdditionalDates && !allDatesHaveLoaded) {
        setDisableCalendarNavigation(true);
        apiGet('/get-compatible-dates', params)
          .then((dateMap) => {
            setAvailableDateMapForAllServiceVehicles((prev) => ({
              ...prev,
              ...dateMap,
            }));
            const optimalSchedules = getOptimalSchedules(dateMap);
            setAvailableDatesToShow(optimalSchedules);
            setLoadingDates(false);
            setDisableCalendarNavigation(false);
          })
          .then(() => {
            // Kick off a full load in the background
            if (!isLoadingAdditionalDates) {
              const paramsForFullLoad = {
                use_new_version: true,
                requested_service_offering_ids: service_offering_ids,
                total_labor_hours,
              };
              if (client_location_id) {
                paramsForFullLoad.client_location_id = client_location_id;
              } else {
                paramsForFullLoad.home_address_id = home_address_id;
              }
              // This loading icon will appear in repeat appointments, but will not block choosing a normal appointment date
              setIsLoadingAdditionalDates(true);
              apiGet('/get-compatible-dates', paramsForFullLoad).then(
                (dateMap) => {
                  setAvailableDateMapForAllServiceVehicles((prev) => ({
                    ...prev,
                    ...dateMap,
                  }));
                  const optimalSchedules = getOptimalSchedules(dateMap);
                  // Do not call setAvailableDatesAndFirstAvailable here because we don't want it to reset the default date
                  setAvailableDatesToShow(optimalSchedules);
                  setIsLoadingAdditionalDates(false);
                  setAllDatesHaveLoaded(true);
                },
              );
            }
          });
      }
    }
  };

  const getOptimalSchedules = (dateMap) => {
    return Object.keys(dateMap).map((d) => {
      if (dateMap[d]?.length > 0) {
        const selectedSchedule =
          dateMap[d].find((s) => s.is_optimal) ||
          dateMap[d].find((s) => s.is_available) ||
          dateMap[d][0];
        return { ...selectedSchedule, date: d };
      }
    });
  };

  const handleMonthChange = (startDateMoment) => {
    getDates(startDateMoment);
  };

  const shouldRenderDateIncompatible = (day) => {
    const formattedDate = moment(day).format('YYYY-MM-DD');
    if (!availableDatesToShow?.map((d) => d.date).includes(formattedDate)) {
      // Don't show disabled dates as incompatible
      return [false, null];
    }
    const selectedSchedule = availableDatesToShow.find(
      (d) => d.date === formattedDate,
    );
    if (!selectedSchedule) {
      // This shouldn't happen, but if we can't find a schedule, show as incompatible
      return [true, 'Date Not Found'];
    }
    const isDateIncompatible =
      !!availableDatesToShow &&
      !shouldDisableDate(day) &&
      !selectedSchedule.is_available;
    return [isDateIncompatible, selectedSchedule.unavailable_reason];
  };

  const keyboardArrowIcon = (direction, disabled, isLoading) =>
    direction === 'right' ? (
      <KeyboardArrowRightIcon
        style={{ marginLeft: isLoading ? '-22px' : 0 }}
        color={disabled ? 'disabled' : 'standard'}
      />
    ) : (
      <KeyboardArrowLeftIcon
        style={{ marginLeft: isLoading ? '-22px' : 0 }}
        color={disabled ? 'disabled' : 'standard'}
      />
    );

  const renderArrowIcons = (direction) => {
    if (disableCalendarNavigation) {
      return (
        <Box style={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress size={22} color="disabled" />
          {keyboardArrowIcon(direction, true, true)}
        </Box>
      );
    }
    return keyboardArrowIcon(direction, disableCalendarNavigation, false);
  };

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      {loadingDates ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
          className={classes.loadingIconBox}
        >
          <CircularProgress size={70} thickness={5} />
        </Box>
      ) : (
        <DatePicker
          autoOk
          disableToolbar
          rightArrowIcon={renderArrowIcons('right')}
          leftArrowIcon={renderArrowIcons('left')}
          variant="static"
          format="YYYY-MM-DD"
          inputVariant="filled"
          margin="normal"
          id="date-picker-inline"
          label="Date picker inline"
          value={selectedDate}
          onChange={(e) => {
            if (availableDatesToShow?.length > 0) handleDateChange(e);
          }}
          onMonthChange={debounce(handleMonthChange, 1000)}
          KeyboardButtonProps={{
            'aria-label': 'change date',
          }}
          shouldDisableDate={shouldDisableDate}
          renderDay={(day, selectDate, isInCurrentMonth, dayComponent) => {
            let dateClass;
            const [isIncompatible, reason] = shouldRenderDateIncompatible(day);

            if (isRecurring) {
              return renderRecurringDatesOnCalendar(
                day,
                dayComponent,
                availableDatesToShow,
                datesToCreate,
                recurringDatesWithConflicts,
                classes,
              );
            }

            if (isInCurrentMonth && isIncompatible) {
              dateClass = `${classes.selectedDateInConflict} ${classes.dateInConflict}`;
              return (
                <Tooltip title={reason}>
                  <Box className={dateClass}>{dayComponent}</Box>
                </Tooltip>
              );
            }

            if (!shouldDisableDate(day)) {
              dateClass = `${classes.selectedDate} ${classes.dateAvailable}`;
            } else if (isInCurrentMonth) dateClass = classes.disableDate;
            return <Box className={dateClass}>{dayComponent}</Box>;
          }}
        />
      )}
    </MuiPickersUtilsProvider>
  );
};

export default AvailabilityWindowsCalendar;
